import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import {
  useGetCropListByParamQuery,
  useGetSeasonsByCountryQuery,
} from '../../../services/mVPGraphQlService';

type Option = { value: string; label: string };

export type CascadeField =
  | 'season'
  | 'region'
  | 'crop'
  | 'hybrid';

type Selection = {
  season?: string;
  region?: string;
  crop?: string;
  hybrid?: string;
};

type Args = Selection & {
  enabled: boolean;
  lockedValues?: Selection;
  clear?: (field: CascadeField) => void;
};

const toOptions = (values: (string | null | undefined)[]) =>
  Array.from(new Set(values.filter(Boolean) as string[])).map(
    (v): Option => ({ value: v, label: v })
  );

const withLocked = (options: Option[], value?: string) =>
  value && !options.some((o) => o.value === value)
    ? [...options, { value, label: value }]
    : options;


export const useCropSeasonOptions = ({
  enabled,
  season,
  region,
  crop,
  hybrid,
  lockedValues,
  clear,
}: Args) => {
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );

  const { data: seasonData, isFetching: seasonsLoading } =
    useGetSeasonsByCountryQuery({ wsId }, { skip: !enabled });

  const { data: cropData, isFetching: cropsLoading } =
    useGetCropListByParamQuery(
      { wsId, season: season || undefined },
      { skip: !enabled }
    );

  const entries = useMemo(
    () => cropData?.data?.GetCropListByParam?.regions ?? [],
    [cropData]
  );

  const seasonOptions = useMemo(
    () =>
      withLocked(
        toOptions(
          seasonData?.data?.GetSeasonsByCountry?.seasons ?? []
        ),
        lockedValues?.season
      ),
    [seasonData, lockedValues?.season]
  );

  const regionOptions = useMemo(
    () =>
      withLocked(
        toOptions(entries.map((e) => e.region)),
        lockedValues?.region
      ),
    [entries, lockedValues?.region]
  );


  const scoped = useMemo(
    () =>
      region ? entries.filter((e) => e.region === region) : entries,
    [entries, region]
  );

  const cropOptions = useMemo(
    () =>
      withLocked(
        toOptions(
          scoped.flatMap((e) =>
            (e.crops ?? []).map((c) => c.cropName)
          )
        ),
        lockedValues?.crop
      ),
    [scoped, lockedValues?.crop]
  );

  const hybridOptions = useMemo(
    () =>
      withLocked(
        crop
          ? toOptions(
            scoped.flatMap((e) =>
              (e.crops ?? [])
                .filter((c) => c.cropName === crop)
                .flatMap((c) => c.hybridProductionCodes ?? [])
            )
          )
          : [],
        lockedValues?.hybrid
      ),
    [scoped, crop, lockedValues?.hybrid]
  );


  useEffect(() => {
    if (!enabled || !clear) return;
    const has = (opts: Option[], v?: string) =>
      opts.some((o) => o.value === v);

    if (season && seasonOptions.length && !has(seasonOptions, season)) {
      clear('season');
    } else if (
      region &&
      regionOptions.length &&
      !has(regionOptions, region)
    ) {
      clear('region');
    } else if (
      crop &&
      cropOptions.length &&
      !has(cropOptions, crop)
    ) {
      clear('crop');
    } else if (hybrid && !crop) {
      clear('hybrid');
    } else if (
      hybrid &&
      hybridOptions.length &&
      !has(hybridOptions, hybrid)
    ) {
      clear('hybrid');
    }
  }, [
    enabled,
    clear,
    season,
    region,
    crop,
    hybrid,
    seasonOptions,
    regionOptions,
    cropOptions,
    hybridOptions,
  ]);

  return {
    seasonOptions,
    regionOptions,
    cropOptions,
    hybridOptions,
    loading: seasonsLoading || cropsLoading,
  };
};
